import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { CalendarCheck, Trophy } from 'lucide-react';
import { PublicOrgSubpageHero } from '@/components/public/PublicOrgSubpageHero';
import { PublicOrgRouteShell } from '@/components/public/PublicOrgRouteShell';
import { PublicPlayEventCard } from '@/components/public/play/PublicPlayEventCard';
import { PublicPlayEventDetailModal } from '@/components/public/play/PublicPlayEventDetailModal';
import { PublicPlayEventsHowItWorks } from '@/components/public/play/PublicPlayEventsHowItWorks';
import { PublicPlayEventsContact } from '@/components/public/play/PublicPlayEventsContact';
import { Badge, Button, Card, CardContent } from '@/components/ui';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useAuthStore } from '@/store/auth';
import { playEventService, type MyPlayEventRegistration, type PublicPlayEventSummary } from '@/services/playEvent';
import {
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_PARTICIPANT_STATUS_BADGE_VARIANT,
  PLAY_PARTICIPANT_STATUS_LABELS,
} from '@/lib/playEventLabels';
import { cn } from '@/lib/utils';

function PublicPlayEventsContent() {
  const { profile } = useOrgPublicProfile();
  const orgSlug = profile?.organization.slug;
  const { p, login } = useOrgPaths(profile?.organization ?? null);
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<PublicPlayEventSummary[]>([]);
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);
  const [myRegistrations, setMyRegistrations] = useState<MyPlayEventRegistration[]>([]);

  const pathFor = p ?? ((sub: string) => sub);
  const selectedEventSlug = searchParams.get('event');

  useEffect(() => {
    if (!orgSlug) {
      return undefined;
    }
    let cancelled = false;
    void playEventService.listPublicEvents(orgSlug).then((res) => {
      if (cancelled) return;
      setEvents(res.success && res.data ? res.data : []);
      setLoadedSlug(orgSlug);
    });
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  useEffect(() => {
    if (!orgSlug || !user) return;
    void playEventService.getMyRegistrations(orgSlug).then((res) => {
      setMyRegistrations(res.success && res.data ? res.data : []);
    });
  }, [orgSlug, user]);

  const visibleRegistrations = orgSlug && user ? myRegistrations : [];

  const handleOpenEvent = (slug: string) => {
    setSearchParams({ event: slug });
  };

  const handleCloseModal = () => {
    setSearchParams({}, { replace: true });
  };

  const showLoading = Boolean(orgSlug) && loadedSlug !== orgSlug;
  const bannerUrl = profile?.organization.bannerUrl;
  const orgName = profile?.organization.name ?? 'the club';

  const eventCountLabel = useMemo(() => {
    if (events.length === 0) return 'No upcoming events';
    if (events.length === 1) return '1 upcoming event';
    return `${events.length} upcoming events`;
  }, [events.length]);

  const { featuredEvents, otherEvents } = useMemo(() => {
    const featured = events.filter((event) => event.isFeatured);
    const featuredIds = new Set(featured.map((event) => event.id));
    const other = events.filter((event) => !featuredIds.has(event.id));
    return { featuredEvents: featured, otherEvents: other };
  }, [events]);

  const gridClassName = cn('grid gap-4', otherEvents.length >= 2 && 'sm:grid-cols-2');

  return (
    <>
      <PublicOrgSubpageHero
        bannerUrl={bannerUrl}
        title="Play events"
        headingId="play-events-hero-heading"
        backLink={{ to: pathFor('/'), label: 'Back to home' }}
      >
        <p className="mt-3 max-w-2xl text-sm text-white/85 sm:text-base">
          Social rotate sessions, round robins, and tournaments at {orgName}. Sign in to register
          online.
        </p>
      </PublicOrgSubpageHero>

      <div className="bg-muted/30">
        <div className="mx-auto max-w-7xl space-y-8 px-4 py-8 sm:px-6 lg:py-10">
          {showLoading ? (
            <div className="flex min-h-[200px] items-center justify-center" aria-busy="true">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
            </div>
          ) : (
            <div className="grid gap-8 lg:grid-cols-[1fr_20rem] xl:grid-cols-[1fr_22rem]">
              <div className="min-w-0 space-y-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-primary">{eventCountLabel}</p>
                    <h2 className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                      Upcoming play
                    </h2>
                    <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                      Tap an event to see format, pricing, and registration options.
                    </p>
                  </div>
                  {!user ? (
                    <Button asChild className="w-full shrink-0 sm:w-auto">
                      <Link to={login(pathFor('/play')) ?? pathFor('/login')}>
                        Sign in to register
                      </Link>
                    </Button>
                  ) : null}
                </div>

                {visibleRegistrations.length > 0 ? (
                  <section aria-labelledby="my-registrations-heading">
                    <div className="mb-3 flex items-center gap-2">
                      <CalendarCheck className="h-4 w-4 text-primary" aria-hidden />
                      <h3 id="my-registrations-heading" className="text-sm font-semibold text-foreground">
                        My registrations
                      </h3>
                    </div>
                    <ul className="flex flex-col gap-2">
                      {visibleRegistrations.map(({ event, participantStatus, paymentStatus }) => (
                        <li key={event.id}>
                          <button
                            type="button"
                            onClick={() => handleOpenEvent(event.slug)}
                            className="group w-full rounded-lg border border-border bg-background px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/30"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-foreground group-hover:text-primary">
                                  {event.name}
                                </p>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                  {format(
                                    event.startsAt instanceof Date
                                      ? event.startsAt
                                      : new Date(event.startsAt),
                                    'EEE d MMM yyyy · h:mm a'
                                  )}
                                  {' · '}
                                  {PLAY_EVENT_FORMAT_LABELS[event.format]}
                                </p>
                              </div>
                              <div className="flex shrink-0 items-center gap-1.5">
                                <Badge
                                  variant={PLAY_PARTICIPANT_STATUS_BADGE_VARIANT[participantStatus]}
                                  className="text-xs"
                                >
                                  {PLAY_PARTICIPANT_STATUS_LABELS[participantStatus]}
                                </Badge>
                                {paymentStatus === 'unpaid' && (
                                  <Badge variant="warning" className="text-xs">
                                    Payment due
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                {events.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center px-6 py-14 text-center">
                      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                        <Trophy className="h-8 w-8 text-primary" aria-hidden />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">No events scheduled yet</h3>
                      <p className="mt-2 max-w-md text-sm text-muted-foreground">
                        {orgName} hasn&apos;t published any play events right now. Check back soon for
                        social sessions and competitions.
                      </p>
                      <Button asChild variant="outline" className="mt-6">
                        <Link to={pathFor('/')}>Back to home</Link>
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {featuredEvents.length > 0 ? (
                      <section aria-labelledby="featured-play-events-heading">
                        <h2 id="featured-play-events-heading" className="sr-only">
                          Featured events
                        </h2>
                        <ul className="flex flex-col gap-4">
                          {featuredEvents.map((event) => (
                            <li key={event.id}>
                              <PublicPlayEventCard
                                event={event}
                                orgRegion={profile?.organization.region ?? null}
                                featured
                                fullWidth
                                onOpen={() => handleOpenEvent(event.slug)}
                              />
                            </li>
                          ))}
                        </ul>
                      </section>
                    ) : null}

                    {otherEvents.length > 0 ? (
                      <section aria-labelledby="play-events-list-heading">
                        <h2 id="play-events-list-heading" className="sr-only">
                          Event list
                        </h2>
                        <ul className={gridClassName}>
                          {otherEvents.map((event) => (
                            <li key={event.id}>
                              <PublicPlayEventCard
                                event={event}
                                orgRegion={profile?.organization.region ?? null}
                                onOpen={() => handleOpenEvent(event.slug)}
                              />
                            </li>
                          ))}
                        </ul>
                      </section>
                    ) : null}
                  </div>
                )}
              </div>

              <aside className="space-y-6" aria-label="Play events information">
                <PublicPlayEventsHowItWorks variant="sidebar" />
                {profile ? (
                  <PublicPlayEventsContact
                    organization={profile.organization}
                    locations={profile.locations}
                  />
                ) : null}
              </aside>
            </div>
          )}
        </div>
      </div>

      {orgSlug ? (
        <PublicPlayEventDetailModal
          orgSlug={orgSlug}
          eventSlug={selectedEventSlug}
          isOpen={Boolean(selectedEventSlug)}
          onClose={handleCloseModal}
          pathFor={pathFor}
          orgRegion={profile?.organization.region ?? null}
        />
      ) : null}
    </>
  );
}

export default function PublicPlayEventsPage() {
  return (
    <PublicOrgRouteShell>
      <PublicPlayEventsContent />
    </PublicOrgRouteShell>
  );
}
